"use client";

import React, { type PropsWithChildren } from "react";

import { useTheme } from "@/hooks/useTheme";
import { EthereumWalletConnectors } from "@dynamic-labs/ethereum";
import { DynamicContextProvider, DynamicUserProfile } from "@dynamic-labs/sdk-react-core";
import { DynamicWagmiConnector } from "@dynamic-labs/wagmi-connector";
import { WagmiProvider } from "wagmi";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HTML5Backend } from "react-dnd-html5-backend";
import { DndProvider } from "react-dnd";
import { SWRConfig } from "swr";

import ExploreReferrerHandler from "@/components/Cred/ReferrerHandler";

import { SupToaster } from "@repo/core-ui";
import dappConfig from "@/../config/dapp.config";
import { config } from "@/../config/wagmi.config";
import spindl from "@spindl-xyz/attribution";

const queryClient = new QueryClient();

if (typeof window !== "undefined") {
  spindl.configure({
    sdkKey: dappConfig.services.spinld,
    debugMode: !dappConfig.isProd,
    host: `${window.location.origin}/ingest`,
  });

  spindl.enableAutoPageViews();
}

export default function ContextDynamic({ children }: PropsWithChildren) {
  const { theme } = useTheme();
  return (
    <DynamicContextProvider
      theme={(["light", "dark"].includes(theme ?? "") ? theme : "dark") as "light" | "dark"}
      settings={{
        walletConnectors: [EthereumWalletConnectors],
        siweStatement: "Log into Superform",
        appLogoUrl: "https://superform.xyz/assets/img/superform-morph.png",
        appName: "Superform App",
        initialAuthenticationMode: "connect-only",
        environmentId: `${dappConfig.services.dynamic}`,
      }}
    >
      <WagmiProvider config={config}>
        <QueryClientProvider client={queryClient}>
          <DynamicWagmiConnector>
            <SWRConfig>
              <DndProvider backend={HTML5Backend}>
                {children}
                <SupToaster position="bottom-center" portal limit={1} />
                <DynamicUserProfile />

                {/* Empty component to handle referrer address */}
                <ExploreReferrerHandler />
              </DndProvider>
            </SWRConfig>
          </DynamicWagmiConnector>
        </QueryClientProvider>
      </WagmiProvider>
    </DynamicContextProvider>
  );
}
